import { defineStore, storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import createLogger from '@/utils/logger'
import { firestore, messagingPromise } from '@/firebase'
import {
  doc,
  onSnapshot,
  updateDoc,
  deleteField,
  DocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore'
import { useCustomerStore } from './customer'
import { deleteToken, getToken, onMessage, type Messaging } from 'firebase/messaging'
import { useToastStore } from './toast'
import { pwaConfig } from '@/utils/pwaTools'
import { usePWAStore } from './pwa'
import {
  effectiveClassReminderGlobalFcm,
  effectiveClassReminderGlobalSms,
  effectiveClassReminderGlobalSmsDaily,
  effectiveConfigVisibleOnUI,
  isCustomerIoChannelOptedOut,
  parseCustomerIoChannelOptOutState,
} from '@gh/common/models'

// const FCM_SW_PATH = '/firebase-messaging-sw.js'
const logger = createLogger('stores/firebaseMessaging.ts')

export const useFirebaseMessagingStore = defineStore('FirebaseMessaging', () => {
  const cusomerStore = useCustomerStore()
  const messaging = ref<Messaging | null>(null) // This will hold the resolved messaging instance
  const toastStore = useToastStore()
  const pwaStore = usePWAStore()
  const { pwaInstallationEnabled } = storeToRefs(pwaStore)
  const customer = computed(() => cusomerStore.customer)
  /** FCM registration token string from `getToken()` (not the Firestore config doc). */
  const registrationToken = ref<string | null>(null)
  const permission = ref<string | null>(null)
  /** Global class-reminder push opt-in (`customer_notification_configuration.classReminderGlobals.fcm`). */
  const classReminderGlobalFcm = ref<boolean>(false)
  /** Global class-reminder SMS opt-in (`customer_notification_configuration.classReminderGlobals.sms`). */
  const classReminderGlobalSms = ref<boolean>(false)
  /** Global daily class-summary SMS digest (`customer_notification_configuration.classReminderGlobals.smsDaily`). */
  const classReminderGlobalSmsDaily = ref<boolean>(false)
  /** Server-driven visibility for per-schedule notification UI (`configVisibleOnUI`). */
  const configVisibleOnUI = ref<boolean>(false)
  /** Customer.io SMS channel opt-out (`customerIoChannelOptOut.sms`). */
  const customerIoSmsOptedOut = ref<boolean>(false)
  // const tokens = ref<Record<string, unknown>>({})
  const lastSavedToken = ref<string | null>(null)
  const initializeFcmScheduled = ref<boolean>(false)
  // const serviceWorkerInstallationScheduled = ref<boolean>(false)

  const enabledAndLoaded = computed(() => {
    return !!(pwaConfig.pwaNotificationsAvailable && messaging.value)
  })

  let unsubscribeNotifConfigSnapshot: (() => void) | null = null

  const notifConfigDoc = computed(() => {
    if (!customer.value) {
      return null
    }
    return doc(firestore, 'customer_notification_configuration', customer.value.id)
  })

  const saveRegistrationTokenToFirestore = async () => {
    if (!enabledAndLoaded.value) {
      logger.error('Notifications not enabled, returning')
      return
    }
    if (!customer.value) {
      logger.error('No customer found, returning')
      return
    }
    if (!registrationToken.value) {
      logger.error('No token found, returning')
      return
    }
    if (!notifConfigDoc.value) {
      logger.error('No notification configuration doc found, returning')
      return
    }
    try {
      logger.log('Saving FCM token to Firestore', {
        customerId: customer.value.id,
      })

      await updateDoc(notifConfigDoc.value, {
        [`tokens.${registrationToken.value}`]: new Date(),
      })
      logger.info('FCM token saved to Firestore', {
        customerId: customer.value.id,
      })
    } catch (error) {
      logger.error('Error saving FCM token to Firestore:', error)
    }
  }

  const removeRegistrationTokenFromFirestore = async () => {
    if (!enabledAndLoaded.value) {
      logger.log('Notifications not enabled, returning')
      return
    }
    if (!customer.value) {
      logger.log('No customer found, returning')
      return
    }
    if (!registrationToken.value) {
      logger.log('No token found, returning')
      return
    }
    if (!notifConfigDoc.value) {
      logger.log('No notification configuration doc found, returning')
      return
    }
    try {
      logger.log('Removing FCM token from Firestore', {
        customerId: customer.value.id,
      })

      await updateDoc(notifConfigDoc.value, {
        [`tokens.${registrationToken.value}`]: deleteField(),
      })
      logger.info('FCM token removed from Firestore', {
        customerId: customer.value.id,
      })
    } catch (error) {
      logger.error('Error removing FCM token from Firestore:', error)
    }
  }

  /**
   * Initialize FCM
   *
   */

  const initializeFcm = async () => {
    if (!pwaInstallationEnabled.value) {
      logger.error('PWA installation not enabled, returning.')
      return
    }

    if (!pwaConfig.pwaNotificationsAvailable) {
      logger.error('Notifications not available on this device, returning.')
      return
    }

    permission.value = Notification.permission

    messagingPromise.then((messagingInstance: Messaging | null) => {
      // toastStore.info(`Firebase Messaging initialized messagingInstance: ${messagingInstance ? 'Yes' : 'No'}`,)
      if (messagingInstance) {
        messaging.value = messagingInstance
        onMessage(messagingInstance, (payload) => {
          logger.info('Message received in foreground.', payload)
          toastStore.debug(
            [payload.data?.title || 'Unknown title', payload.data?.body || 'Unknown body'].join(
              ' | ',
            ),
            300,
          )
        })
      } else {
        logger.warn('Firebase Messaging is not supported in this browser.')
        // messagingSupported.value = false
      }
    })
  }

  const scheduleInitializeFcm = () => {
    logger.log('scheduleInitializeFcm: Scheduling FCM initialization')
    initializeFcmScheduled.value = true
  }

  watch(
    [pwaInstallationEnabled, initializeFcmScheduled],
    async (newVal) => {
      const [pwaInstallationEnabledValueNew, initializeFcmScheduledValueNew] = newVal

      logger.log('watch: pwaInstallationEnabled', pwaInstallationEnabledValueNew)
      logger.log('watch: scheduleInitializeFcm', initializeFcmScheduledValueNew)

      if (!pwaInstallationEnabledValueNew) {
        logger.log('watch: PWA installation not enabled, returning.')
        return
      }
      if (!pwaConfig.pwaNotificationsAvailable) {
        logger.log('watch: Notifications not available on this device, returning.')
        return
      }
      if (initializeFcmScheduledValueNew) {
        logger.log('watch: Initializing FCM')
        toastStore.debug('watch: Initializing FCM')
        await initializeFcm()
      }

      initializeFcmScheduled.value = false
    },
    { immediate: true },
  )

  const requestPermission = async () => {
    if (!enabledAndLoaded.value) {
      logger.log('Notifications not enabled, returning')
      return
    }
    if (permission.value === 'granted') {
      return
    }

    const newPermission = await Notification.requestPermission()
    permission.value = newPermission === 'granted' ? 'granted' : 'denied'
    if (permission.value === 'granted') {
      toastStore.debug('Notification permission granted.')
      logger.info('Notification permission granted.')
      // await getFcmToken()
    } else {
      toastStore.debug('Unable to get permission to notify.')
      logger.warn('Unable to get permission to notify.')
    }
  }

  const getFcmToken = async () => {
    if (!enabledAndLoaded.value) {
      logger.log('getFcmToken:Notifications not enabled, returning')
      toastStore.debug('getFcmToken: Notifications not enabled, returning')
      return
    }
    if (permission.value !== 'granted') {
      logger.warn('getFcmToken: Notification permission not granted, returning')
      toastStore.debug('getFcmToken: Notification permission not granted, returning')
      return
    }
    if (!messaging.value) {
      logger.warn('getFcmToken: messaging not initialized')
      toastStore.debug('getFcmToken: messaging not initialized')
      return
    }

    toastStore.debug(`getFcmToken: getting fcm token`)
    // By explicitly registering the dedicated FCM service worker here, we ensure
    // that Firebase uses the correct one, avoiding conflicts with the main app SW.
    // This is the key to fixing the "installations/request-failed" error.
    const registration = await registerWorker()

    const tokenFromSdk = await getToken(messaging.value, {
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      serviceWorkerRegistration: registration,
    })

    toastStore.debug(`getFcmToken: registration token ${tokenFromSdk?.slice(-8)}`)
    if (tokenFromSdk) {
      logger.info('getFcmToken: FCM registration token obtained')
      registrationToken.value = tokenFromSdk
      // TODO: Send this token to your server to associate it with the current user
    } else {
      toastStore.debug(
        'getFcmToken: No registration token available. Request permission to generate one.',
      )
      logger.warn(
        'getFcmToken: No registration token available. Request permission to generate one.',
      )
      // toastStore.debug('getFcmToken: No registration token available. Request permission to generate one.')
    }
  }

  const registerWorker = async () => {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/firebase-cloud-messaging-push-scope',
    })
    await registration
      .update()
      .catch((error) => {
        logger.error('Error updating service worker', error)
      })
      .then(() => {
        logger.log('Service worker updated')
      })
    return registration
  }
  const cleanupNotifConfigSnapshot = () => {
    if (unsubscribeNotifConfigSnapshot) {
      unsubscribeNotifConfigSnapshot()
      unsubscribeNotifConfigSnapshot = null
    }
  }
  /**
   * Load `classReminderGlobals` from `customer_notification_configuration` and subscribe to changes.
   * Independent of FCM/messaging (`enabledAndLoaded`) so SMS global state hydrates on every browser.
   */
  watch(
    [customer, notifConfigDoc],
    async ([newCustomerValue, newNotifConfigDocValue]) => {
      cleanupNotifConfigSnapshot()

      if (!newCustomerValue?.id || !newNotifConfigDocValue) {
        classReminderGlobalFcm.value = false
        classReminderGlobalSms.value = false
        classReminderGlobalSmsDaily.value = false
        configVisibleOnUI.value = false
        customerIoSmsOptedOut.value = false
        if (!newCustomerValue?.id) {
          logger.log('No customer id, cleared classReminderGlobals listener')
        }
        return
      }

      unsubscribeNotifConfigSnapshot = onSnapshot(
        newNotifConfigDocValue,
        (docSnap: DocumentSnapshot<DocumentData>) => {
          const docData = docSnap.data() as Record<string, unknown> | undefined
          classReminderGlobalFcm.value = effectiveClassReminderGlobalFcm(docData)
          classReminderGlobalSms.value = effectiveClassReminderGlobalSms(docData)
          classReminderGlobalSmsDaily.value = effectiveClassReminderGlobalSmsDaily(docData)
          configVisibleOnUI.value =
            import.meta.env.VITE_NOTIFICATIONS_CONFIG_ON_UI_HIDDEN === 'true'
              ? false
              : effectiveConfigVisibleOnUI(docData)
          customerIoSmsOptedOut.value = isCustomerIoChannelOptedOut(
            parseCustomerIoChannelOptOutState(docData?.customerIoChannelOptOut),
            'sms',
          )
        },
      )
    },
    { immediate: true },
  )

  watch(customer, async (newCustomerValue) => {
    if (!newCustomerValue) {
      registrationToken.value = null
      lastSavedToken.value = null // Reset when customer changes
      classReminderGlobalFcm.value = false
      classReminderGlobalSms.value = false
      classReminderGlobalSmsDaily.value = false
      configVisibleOnUI.value = false
      customerIoSmsOptedOut.value = false
    }
  })

  const notificationsEnabled = computed(() => {
    return (
      pwaInstallationEnabled.value && classReminderGlobalFcm.value && permission.value === 'granted'
    )
  })

  /**
   * Track the last saved token to prevent unnecessary saves
   *  getFcmToken and saveFcmTokenToFirestore are called here to ensure the token is saved to Firestore
   *
   *  This is used to track the last saved token to prevent unnecessary saves
   *  when the user is not logged in.
   *
   *
   */
  watch(
    [customer, registrationToken, permission, classReminderGlobalFcm],
    async (newVal) => {
      const [customerValue, tokenValue, permissionValue, classReminderGlobalFcmValue] = newVal
      if (!enabledAndLoaded.value) {
        logger.log('Notifications not enabled, returning')
        return
      }

      if (!customerValue) {
        return
      }

      if (!permissionValue || permissionValue !== 'granted') {
        toastStore.debug(`permission not granted ${permissionValue}, returning`)
        return
      }

      if (!classReminderGlobalFcmValue) {
        toastStore.debug(`class reminder global FCM off, returning`)
        return
      }

      if (!tokenValue || (tokenValue !== lastSavedToken.value && lastSavedToken.value)) {
        toastStore.debug(`saving fcm token to firestore`)

        await getFcmToken()
        await saveRegistrationTokenToFirestore()
        lastSavedToken.value = tokenValue
      }
    },
    { immediate: true },
  )

  const removeFcmToken = async () => {
    messagingPromise.then((messagingInstance: Messaging | null) => {
      if (messagingInstance) {
        deleteToken(messagingInstance).catch((err: unknown) => {
          logger.error('Error deleting token on uninstall', err)
        })
      }
    })
  }

  function signOut() {
    registrationToken.value = null
    lastSavedToken.value = null
    classReminderGlobalFcm.value = false
    classReminderGlobalSms.value = false
    classReminderGlobalSmsDaily.value = false
    configVisibleOnUI.value = false
    customerIoSmsOptedOut.value = false

    cleanupNotifConfigSnapshot()
    removeFcmToken()
  }

  async function beforeSignOut() {
    await removeRegistrationTokenFromFirestore()
  }

  return {
    registrationToken,
    classReminderGlobalFcm,
    classReminderGlobalSms,
    classReminderGlobalSmsDaily,
    configVisibleOnUI,
    customerIoSmsOptedOut,
    signOut,
    beforeSignOut,
    initializeFcm,
    scheduleInitializeFcm,
    requestPermission,
    permission,
    enabledAndLoaded,
    // installServiceWorker,
    // scheduleServiceWorkerInstallation,
    // updateServiceWorker,
    // uninstallServiceWorker,
    notificationsEnabled,
    messaging,
  }
})
